import React from "react";
import { Box, Typography, Divider } from "@mui/material";
import moment from "moment";
import { formatReceiptOrderNumber } from "../../utils/orderNumberFormat";

const PAYMENT_METHOD_LABELS = {
  none: "No Payment",
  cash: "Cash",
  card: "Card",
  loyalty: "Loyalty",
  split: "Split",
  online: "Online",
  lankaqr: "LankaQR",
  other: "Other",
};

const getPaymentMethodLabel = (payment) => {
  const key = payment?.paymentMethod || payment?.method || "";
  if (!key) return "Payment";
  return (
    PAYMENT_METHOD_LABELS[key] ||
    key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
  );
};

/**
 * CustomerBill Component
 * Professional thermal printer receipt for customers
 */
const CustomerBill = ({
  orderResult,
  companyInfo,
  currency = "Rs.",
  paperSize = "80mm",
}) => {
  if (!orderResult) return null;

  const formatDate = (dateString) => {
    return moment(dateString).format("MMM DD, YYYY hh:mm A");
  };

  // Calculate totals
  const subtotal = parseFloat(orderResult.subtotal || 0);
  const serviceCharge = parseFloat(orderResult.serviceChargeAmount || 0);
  const vatAmount = parseFloat(orderResult.vatAmount || 0);
  const discountAmount = parseFloat(orderResult.discountAmount || 0);
  const totalAmount = parseFloat(orderResult.totalAmount || 0);
  const displayOrderNumber = formatReceiptOrderNumber(
    orderResult.orderNumber,
    orderResult.id,
  );

  return (
    <Box
      sx={{
        width: paperSize,
        fontFamily: "monospace",
        fontSize: "12px",
        p: 2,
        bgcolor: "white",
        color: "black",
        "@media print": {
          width: paperSize,
        },
      }}
    >
      <style>{`
        @page {
          size: ${paperSize} auto;
          margin: 0;
        }
        @media print {
          body { margin: 0; }
        }
      `}</style>
      {/* Header */}
      <Box sx={{ textAlign: "center", mb: 2 }}>
        {companyInfo?.name && (
          <Typography
            sx={{
              fontSize: "16px",
              fontWeight: 700,
              textTransform: "uppercase",
              mb: 0.5,
            }}
          >
            {companyInfo.name}
          </Typography>
        )}
        {companyInfo?.address && (
          <Typography sx={{ fontSize: "10px", mb: 0.5 }}>
            {companyInfo.address}
          </Typography>
        )}
        {companyInfo?.phone && (
          <Typography sx={{ fontSize: "10px", mb: 0.5 }}>
            Tel: {companyInfo.phone}
          </Typography>
        )}
        {companyInfo?.taxId && (
          <Typography sx={{ fontSize: "10px" }}>
            Tax ID: {companyInfo.taxId}
          </Typography>
        )}
      </Box>

      <Divider sx={{ borderStyle: "dashed", borderWidth: 1, my: 1.5 }} />

      {/* Order Info */}
      <Box sx={{ mb: 2 }}>
        <Box
          sx={{
            textAlign: "center",
            py: 1.25,
            px: 1,
            mb: 1.25,
            border: "2px solid black",
          }}
        >
          <Typography
            sx={{
              fontSize: "9px",
              fontWeight: 700,
              letterSpacing: 1.2,
              mb: 0.25,
            }}
          >
            ORDER NUMBER
          </Typography>
          <Typography
            sx={{
              fontSize: "28px",
              fontWeight: 900,
              lineHeight: 1.1,
              letterSpacing: 0.5,
            }}
          >
            {displayOrderNumber}
          </Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            mb: 0.5,
          }}
        >
          <Typography sx={{ fontSize: "11px" }}>Date:</Typography>
          <Typography sx={{ fontSize: "11px" }}>
            {formatDate(orderResult.createdAt)}
          </Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            mb: 0.5,
          }}
        >
          <Typography sx={{ fontSize: "11px" }}>Type:</Typography>
          <Typography
            sx={{
              fontSize: "11px",
              fontWeight: 700,
              textTransform: "uppercase",
            }}
          >
            {orderResult.orderType?.replace("-", " ")}
          </Typography>
        </Box>

        {orderResult.Table && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              mb: 0.5,
            }}
          >
            <Typography sx={{ fontSize: "11px" }}>Table:</Typography>
            <Typography sx={{ fontSize: "11px", fontWeight: 900 }}>
              {orderResult.Table.tableNumber}
              {orderResult.Table.location && ` (${orderResult.Table.location})`}
            </Typography>
          </Box>
        )}

        {orderResult.customerName && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              mb: 0.5,
            }}
          >
            <Typography sx={{ fontSize: "11px" }}>Customer:</Typography>
            <Typography sx={{ fontSize: "11px" }}>
              {orderResult.customerName}
            </Typography>
          </Box>
        )}

        {orderResult.customerMobile && (
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Typography sx={{ fontSize: "11px" }}>Mobile:</Typography>
            <Typography sx={{ fontSize: "11px" }}>
              {orderResult.customerMobile}
            </Typography>
          </Box>
        )}

        {/* Order-level Special Instructions */}
        {orderResult.specialInstructions && (
          <Box
            sx={{
              mt: 1.5,
              p: 1,
              bgcolor: "#fff3cd",
              borderRadius: 1,
              border: "1px solid #ffc107",
            }}
          >
            <Typography
              sx={{
                fontSize: "10px",
                fontWeight: 700,
                mb: 0.5,
                textTransform: "uppercase",
              }}
            >
              ⚠ Special Instructions:
            </Typography>
            <Typography sx={{ fontSize: "10px", fontStyle: "italic" }}>
              {orderResult.specialInstructions}
            </Typography>
          </Box>
        )}
      </Box>

      <Divider sx={{ borderStyle: "dashed", borderWidth: 1, my: 1.5 }} />

      {/* Order Items */}
      <Box sx={{ mb: 2 }}>
        <Typography
          sx={{
            fontSize: "12px",
            fontWeight: 700,
            mb: 1,
            textTransform: "uppercase",
          }}
        >
          ITEMS
        </Typography>

        {orderResult.OrderItems?.map((orderItem, index) => {
          const itemPrice = parseFloat(orderItem.price || 0);
          const totalPrice = parseFloat(orderItem.totalPrice || 0);

          return (
            <Box key={index} sx={{ mb: 1.5 }}>
              {/* Item Name and Quantity */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mb: 0.5,
                }}
              >
                <Typography sx={{ fontSize: "11px", fontWeight: 700 }}>
                  {orderItem.MenuItem?.name || orderItem.Offer?.name || "Item"}
                </Typography>
                <Typography sx={{ fontSize: "11px", fontWeight: 700 }}>
                  {currency} {totalPrice.toFixed(2)}
                </Typography>
              </Box>

              {/* Item Details */}
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography
                  sx={{ fontSize: "10px", color: "#666", fontStyle: "italic" }}
                >
                  {orderItem.quantity} × {currency} {itemPrice.toFixed(2)}
                  {orderItem.ItemVariation &&
                    ` (${orderItem.ItemVariation.name})`}
                </Typography>
              </Box>

              {/* Modifiers */}
              {orderItem.OrderItemModifiers &&
                orderItem.OrderItemModifiers.length > 0 && (
                  <Box sx={{ ml: 1, mt: 0.5 }}>
                    {orderItem.OrderItemModifiers.map((mod, modIndex) => (
                      <Box
                        key={modIndex}
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                        }}
                      >
                        <Typography sx={{ fontSize: "10px", color: "#666" }}>
                          + {mod.ModifierOption?.name || "Modifier"}
                        </Typography>
                        <Typography sx={{ fontSize: "10px", color: "#666" }}>
                          {currency} {parseFloat(mod.price || 0).toFixed(2)}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}

              {/* Special Instructions */}
              {orderItem.specialInstructions && (
                <Typography
                  sx={{
                    fontSize: "10px",
                    color: "#666",
                    fontStyle: "italic",
                    mt: 0.5,
                    ml: 1,
                  }}
                >
                  Note: {orderItem.specialInstructions}
                </Typography>
              )}
            </Box>
          );
        })}
      </Box>

      <Divider sx={{ borderStyle: "solid", borderWidth: 1, my: 1.5 }} />

      {/* Totals */}
      <Box sx={{ mb: 2 }}>
        {/* Subtotal */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            mb: 0.5,
          }}
        >
          <Typography sx={{ fontSize: "11px" }}>Subtotal:</Typography>
          <Typography sx={{ fontSize: "11px" }}>
            {currency} {subtotal.toFixed(2)}
          </Typography>
        </Box>

        {/* Discount */}
        {discountAmount > 0 && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              mb: 0.5,
            }}
          >
            <Typography sx={{ fontSize: "11px", fontWeight: 700 }}>
              Discount:
            </Typography>
            <Typography sx={{ fontSize: "11px", fontWeight: 700 }}>
              - {currency} {discountAmount.toFixed(2)}
            </Typography>
          </Box>
        )}

        {/* Service Charge */}
        {serviceCharge > 0 && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              mb: 0.5,
            }}
          >
            <Typography sx={{ fontSize: "11px" }}>Service Charge:</Typography>
            <Typography sx={{ fontSize: "11px" }}>
              {currency} {serviceCharge.toFixed(2)}
            </Typography>
          </Box>
        )}

        {/* VAT */}
        {vatAmount > 0 && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              mb: 0.5,
            }}
          >
            <Typography sx={{ fontSize: "11px" }}>
              VAT ({companyInfo?.vatRate || 0}%):
            </Typography>
            <Typography sx={{ fontSize: "11px" }}>
              {currency} {vatAmount.toFixed(2)}
            </Typography>
          </Box>
        )}

        <Divider sx={{ borderStyle: "dashed", borderWidth: 1, my: 1 }} />

        {/* Total */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            p: 0.5,
            bgcolor: "#f0f0f0",
          }}
        >
          <Typography
            sx={{
              fontSize: "14px",
              fontWeight: 700,
              textTransform: "uppercase",
            }}
          >
            TOTAL:
          </Typography>
          <Typography sx={{ fontSize: "14px", fontWeight: 900 }}>
            {currency} {totalAmount.toFixed(2)}
          </Typography>
        </Box>
      </Box>

      {/* Payment Info */}
      {orderResult.Payments && orderResult.Payments.length > 0 && (
        <>
          <Divider sx={{ borderStyle: "dashed", borderWidth: 1, my: 1.5 }} />
          <Box sx={{ mb: 2 }}>
            <Typography
              sx={{
                fontSize: "11px",
                fontWeight: 700,
                mb: 0.5,
                textTransform: "uppercase",
              }}
            >
              PAYMENT METHOD:
            </Typography>
            {orderResult.Payments.map((payment, index) => (
              <Box
                key={index}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mb: 0.5,
                }}
              >
                <Typography
                  sx={{
                    fontSize: "11px",
                    textTransform: "capitalize",
                  }}
                >
                  {getPaymentMethodLabel(payment)}:
                </Typography>
                <Typography sx={{ fontSize: "11px" }}>
                  {currency} {parseFloat(payment.amount || 0).toFixed(2)}
                </Typography>
              </Box>
            ))}
          </Box>
        </>
      )}

      <Divider sx={{ borderStyle: "dashed", borderWidth: 1, my: 1.5 }} />

      {/* Footer */}
      <Box sx={{ textAlign: "center", mt: 2 }}>
        <Typography
          sx={{
            fontSize: "12px",
            fontWeight: 700,
            mb: 1,
          }}
        >
          Thank You for Your Order!
        </Typography>
        <Typography sx={{ fontSize: "10px", mb: 0.5 }}>
          Please visit us again
        </Typography>
        {companyInfo?.website && (
          <Typography sx={{ fontSize: "10px" }}>
            {companyInfo.website}
          </Typography>
        )}
      </Box>

      <Box sx={{ textAlign: "center", mt: 2 }}>
        <Typography
          sx={{
            fontSize: "18px",
            fontWeight: 900,
            fontFamily: "monospace",
            letterSpacing: 1,
          }}
        >
          {displayOrderNumber}
        </Typography>
      </Box>
    </Box>
  );
};

export default CustomerBill;
